/**
 * Turkish locative case (bulunma hali) helper for city/proper nouns.
 *
 * Rules (TDK):
 *  1. Word ends with hard consonant (p, ç, t, k, s, ş, h, f) → -ta / -te
 *  2. Otherwise → -da / -de
 *  3. Vowel harmony on last vowel:
 *       back vowels (a, ı, o, u) → -a suffix
 *       front vowels (e, i, ö, ü) → -e suffix
 *  4. When the noun ends with a vowel, Turkish sometimes inserts a buffer
 *     "n" (Kocaeli'nde). This helper adds the buffer for a natural feel.
 *
 * Examples:
 *   İzmir   → İzmir'de
 *   Ankara  → Ankara'da
 *   Bursa   → Bursa'da
 *   Sinop   → Sinop'ta
 *   Uşak    → Uşak'ta
 *   Kocaeli → Kocaeli'nde
 *   Muğla   → Muğla'da
 */

const HARD_CONSONANTS = new Set(['p', 'ç', 't', 'k', 's', 'ş', 'h', 'f']);
const BACK_VOWELS = new Set(['a', 'ı', 'o', 'u']);
const FRONT_VOWELS = new Set(['e', 'i', 'ö', 'ü']);
const VOWELS = new Set([...BACK_VOWELS, ...FRONT_VOWELS]);

const lower = (s) => (s || '').toLocaleLowerCase('tr-TR');

/**
 * Returns the locative suffix (starts with an apostrophe) for a proper noun.
 * @param {string} name e.g. "İzmir"
 * @returns {string} e.g. "'de", "'nde", "'ta"
 */
export function locativeSuffix(name) {
  const n = lower(name).trim();
  if (!n) return "'de";

  // 1. Find last vowel to pick a/e via harmony
  let lastVowel = '';
  for (let i = n.length - 1; i >= 0; i -= 1) {
    if (VOWELS.has(n[i])) {
      lastVowel = n[i];
      break;
    }
  }
  const isFront = FRONT_VOWELS.has(lastVowel);
  const vowel = isFront ? 'e' : 'a';

  // 2. Consonant choice (based on very last character)
  const lastChar = n[n.length - 1];
  const consonant = HARD_CONSONANTS.has(lastChar) ? 't' : 'd';

  // NOTE: TDK — locative case takes NO buffer "n" for proper nouns ending in
  // a vowel. Use "Ankara'da", "Kayseri'de", "Kocaeli'de" (not "'nde").
  return `'${consonant}${vowel}`;
}

/**
 * "Şehirim'de" (locative form) — city name + correct suffix.
 * @example locativeCity('İzmir') → "İzmir'de"
 */
export function locativeCity(name) {
  const clean = (name || '').trim();
  if (!clean) return '';
  return `${clean}${locativeSuffix(clean)}`;
}

/**
 * Adjective form "-daki / -deki / -taki / -teki".
 * @example locativeAdjectiveSuffix('İzmir') → "'deki"
 */
export function locativeAdjectiveSuffix(name) {
  const s = locativeSuffix(name); // ends in 'a' or 'e'
  return `${s}ki`;
}

/**
 * "Şehirim'deki" (adjective form) — city name + correct -daki/-deki/-taki/-teki suffix.
 * @example locativeAdjectiveCity('İzmir') → "İzmir'deki"
 * @example locativeAdjectiveCity('Ankara') → "Ankara'daki"
 * @example locativeAdjectiveCity('Sinop') → "Sinop'taki"
 */
export function locativeAdjectiveCity(name) {
  const clean = (name || '').trim();
  if (!clean) return '';
  return `${clean}${locativeAdjectiveSuffix(clean)}`;
}
